import { createSlice } from '@reduxjs/toolkit';
import webServices from '../../services/webServices';
// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  isLoadingBtnHom: false,
  isLoadingBtnUpld: false,
  error: false,
  success: '',
  address: {},
  home: {},
  masterStateList: []
};

const slice = createSlice({
  name: 'residence',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    startLoadingHom(state) {
      state.isLoadingBtnHom = true;
    },

    startLoadingUpld(state) {
      state.isLoadingBtnUpld = true;
    },

    // SUCCESS MESSAGE
    success(state, action) {
      state.success = action.payload;
      state.isLoadingBtnUpld = false;
      state.isLoadingBtnHom = false;
    },

    // HAS ERROR
    hasError(state, action) {
      // Every loading flag, not just the page one. A failed save only cleared
      // isLoading, so the Save button on the property form kept spinning and
      // the form looked like it was still working.
      state.isLoading = false;
      state.isLoadingBtnHom = false;
      state.isLoadingBtnUpld = false;
      state.error = action.payload;
    },

    // GET ADDRESS DETAILS
    getAddressSuccess(state, action) {
      state.isLoading = false;
      state.address = action.payload;
      state.success = '';
    },

    // GET HOME DETAILS
    getHomeSuccess(state, action) {
      state.isLoadingBtnHom = false;
      state.home = action.payload;
      state.success = '';
    },

    // GET MASTER STATE LIST
    getStateListSuccess(state, action) {
      state.isLoading = false;
      state.masterStateList = action.payload.sort((a, b) => (a.stateName > b.stateName ? 1 : -1));
      state.success = '';
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
export const {
  success,
  hasError,
  startLoading,
  isLoadingBtnHom,
  startLoadingHom,
  isLoadingBtnUpld,
  startLoadingUpld,
  createAddressSuccess,
  getAddressSuccess,
  getHomeSuccess,
  getStateListSuccess
} = slice.actions;

// -------------------- START ADDRESS --------------------------------

export function getAddress() {
  return async (dispatch) => {
    try {
      const resp = await webServices.getAddress({});
      if (resp && resp.success === true) {
        dispatch(getAddressSuccess(resp.data));
      } else {
        dispatch(hasError(resp.message));
      }
    } catch (error) {
      dispatch(hasError(error));
    }
  };
}

export function createAddress(data) {
  // eslint-disable-next-line consistent-return
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      let msg;
      let resp;
      // `data` is the caller's formik values object. `delete data.id` used to
      // reach in and mutate it, so after one save the form no longer knew which
      // record it was editing and the next save would have CREATED a duplicate.
      const { id, ...payload } = data;
      if (id) {
        resp = await webServices.updateAddress(id, payload);
        msg = 'Address updated';
        if (resp && resp.success === true) {
          dispatch(success(msg));
          dispatch(getAddress());
          return true;
        }
        dispatch(hasError(resp && resp.message));
        return false;
      }
      resp = await webServices.createAddress(payload);
      msg = 'Address saved';
      // Card 108 — the address already exists and a prior owner left items with
      // the residence. Don't claim it yet; hand the import payload back to the
      // caller so it can show the import table.
      if (resp && resp.moveImport === true) {
        return resp;
      }
      if (resp && resp.success === true) {
        dispatch(success(msg));
        dispatch(getAddress());
        return true;
      }
      // dispatch(hasError(resp.message));
      return false;
    } catch (error) {
      dispatch(hasError(error));
    }
  };
}

export function getStateList() {
  return async (dispatch) => {
    try {
      // States now come from the zipcodes table (card 23) rather than the
      // masterData `state` type. The response shape ({ name, abbreviation }) is
      // unchanged, so downstream state dropdowns need no changes.
      const resp = await webServices.getStates();
      if (resp && resp.success === true) {
        dispatch(getStateListSuccess(resp.data));
      } else {
        dispatch(hasError(resp.message));
      }
    } catch (error) {
      dispatch(hasError(error));
    }
  };
}
// -------------------- END ADDRESS ------------------------------------

// -------------------- START HOME -------------------------------------

export function getHome() {
  return async (dispatch) => {
    try {
      const resp = await webServices.getHome({});
      if (resp && resp.success === true) {
        dispatch(getHomeSuccess(resp.data));
      } else {
        dispatch(hasError(resp.message));
      }
    } catch (error) {
      dispatch(hasError(error));
    }
  };
}

export function createHome(data) {
  // eslint-disable-next-line consistent-return
  return async (dispatch) => {
    dispatch(startLoadingHom());
    try {
      let msg;
      let resp;
      // Destructure rather than `delete data.id` — see createAddress above.
      const { id, ...payload } = data;
      if (id) {
        resp = await webServices.updateHome(id, payload);
        msg = 'Property details updated';
      } else {
        resp = await webServices.createHome(payload);
        msg = 'Property details saved';
      }

      if (resp && resp.success === true) {
        dispatch(success(msg));
        dispatch(getHome());
        return true;
      }
      // The button spinner is only cleared by success() or getHomeSuccess(), so
      // a failed save used to leave it spinning forever.
      dispatch(hasError(resp && resp.message));
      return false;
    } catch (error) {
      dispatch(hasError(error && error.message));
      return false;
    }
  };
}

export function uploadFiles(otherFiles, id) {
  return async (dispatch) => {
    dispatch(startLoadingUpld());
    try {
      const resp = await webServices.fileUploadHome(id, { otherFiles });
      if (resp && resp.success === true) {
        dispatch(success('Uploaded success'));
        dispatch(getHome());
      } else {
        dispatch(hasError(resp.message));
      }
    } catch (error) {
      dispatch(hasError(error));
    }
  };
}

export function deleteFiles(fileURL, id) {
  return async (dispatch) => {
    try {
      const resp = await webServices.fileDeleteHome(id, { fileURL });
      if (resp && resp.success === true) {
        dispatch(success('Deleted success'));
        dispatch(getHome());
      } else {
        dispatch(hasError(resp.message));
      }
    } catch (error) {
      dispatch(hasError(error));
    }
  };
}

// -------------------- END HOME -------------------------------------
